import React, { useEffect } from "react";
import { useQuery as useConvexQuery, useConvexAuth } from "convex/react";
import { refs } from "../convex/apiRefs";
import { CONVEX_CONFIGURED } from "../utility/backendConfig";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Crew cells' LTR scores (#695) — live from Convex (crewScores), for the grid's crews.
//
// Same shape as the FAV Inbox fetcher: the subscription lives in its own tiny component
// inside its own boundary, so a refused/failed Convex query only hides the score line —
// with no boundary it would unmount the WHOLE app (pattern_convex_arg_mismatch_blanks_the_app).
// Works for mongoOnly users too (the app-wide ConvexProvider is mounted whenever Convex
// is configured).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const MAX_PAYROLLS = 400; // the query's own cap (MAX_PAYROLLS_PER_SCORE_READ)

function Fetcher({ companyCode, payrollNos, onScores }) {
  const { isAuthenticated } = useConvexAuth();
  const ready = isAuthenticated && !!companyCode && payrollNos.length > 0;
  const rows = useConvexQuery(
    refs.crewLtrForCrews,
    ready ? { companyCode, payrollNos: payrollNos.slice(0, MAX_PAYROLLS) } : "skip"
  );
  useEffect(() => {
    if (!ready) onScores(new Map());
    else if (rows) onScores(new Map(rows.map((r) => [r.payrollNo, r])));
  }, [ready, rows, onScores]);
  return null;
}

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.warn("%cCrewScores: Convex query failed — LTR line hidden", "color: #f97316; font-weight: bold;", error);
    this.props.onScores(new Map());
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** TEMPORARY (Leland 2026-10-16: "lets temp-hide LTR scores in Prod"): no score line on the PROD build. */
export function crewScoresShown(appEnv) {
  return appEnv !== "production";
}

export default function CrewScoresFetcher({ companyCode, payrollNos, onScores }) {
  if (!CONVEX_CONFIGURED || !crewScoresShown(process.env.REACT_APP_ENV)) return null;
  return (
    <Boundary onScores={onScores}>
      <Fetcher companyCode={companyCode} payrollNos={payrollNos} onScores={onScores} />
    </Boundary>
  );
}
